/**
 * Main Drag & Drop Hook
 * 
 * Encapsulates ALL drag & drop behavior for mindmap reordering.
 * Returns everything needed to wire into MindmapCanvas with minimal changes.
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { createLogger } from '@core/be/platform/logging/Logger';
import { useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import type { DragStartEvent, DragMoveEvent, DragEndEvent } from '@dnd-kit/core';
import type { Mindmap, MindmapNode } from '@/features/mindmap/core/MindmapTypes';
import type { DragState, CollisionNode, Viewport } from './types';
import { MidlineCollisionDetector } from './MidlineCollisionDetector';

export interface UseMindmapDragAndDropProps {
  /** Current mindmap document */
  doc: Mindmap;
  
  /** Node positions from ELK layout (flow coordinates) */
  positions: Record<string, { x: number; y: number }>;
  
  /** Node dimensions from DOM measurements */
  nodeDimensions: Record<string, { width: number; height: number }>;
  
  /** Callback to commit move operation */
  onMove: (draggedId: string, targetParentId: string | null, targetIndex: number) => void;
  
  /** Get current viewport state */
  getViewport: () => Viewport;
  
  /** Get pane DOM element for coordinate conversion */
  getPaneElement: () => HTMLElement | null;
  
  /** Pan viewport by delta in screen pixels (optional, improves UX near edges) */
  panViewportBy?: (dx: number, dy: number) => void;
  
  /** Whether drag is currently enabled (disable during editing) */
  enabled?: boolean;
}

export function useMindmapDragAndDrop({
  doc,
  positions,
  nodeDimensions,
  onMove,
  getViewport,
  getPaneElement,
  panViewportBy,
  enabled = true,
}: UseMindmapDragAndDropProps) {
  // Logger (namespaced)
  const logRef = useRef(createLogger('mindmap:dnd'));
  
  // ============================================================================
  // State
  // ============================================================================
  
  const [dragState, setDragState] = useState<DragState | null>(null);
  const dragStartClientRef = useRef<{ x: number; y: number } | null>(null);
  const collisionDetectorRef = useRef<MidlineCollisionDetector | null>(null);
  const autoPanRafRef = useRef<number | null>(null);
  
  // Throttle state for sticky slot detection
  const lastSlotKeyRef = useRef<string | null>(null);
  
  // ============================================================================
  // Coordinate Conversion
  // ============================================================================
  
  const clientToFlow = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const pane = getPaneElement();
      const viewport = getViewport();
      if (!pane) return { x: 0, y: 0 };
      
      const rect = pane.getBoundingClientRect();
      const screenX = clientX - rect.left;
      const screenY = clientY - rect.top;
      const flowX = (screenX - viewport.x) / viewport.zoom;
      const flowY = (screenY - viewport.y) / viewport.zoom;
      
      return { x: flowX, y: flowY };
    },
    [getPaneElement, getViewport]
  );
  
  // ============================================================================
  // Collision Detection Setup
  // ============================================================================
  
  const buildCollisionNodes = useCallback((): CollisionNode[] => {
    return doc.nodes.map(node => ({
      id: node.id,
      parentId: node.parentId,
      order: node.order,
      position: positions[node.id] ?? { x: 0, y: 0 },
      dimensions: nodeDimensions[node.id] ?? { width: 128, height: 40 },
    }));
  }, [doc.nodes, positions, nodeDimensions]);
  
  // ============================================================================
  // Drag Handlers
  // ============================================================================
  
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (!enabled) return;
      
      const rawId = String(event.active.id);
      if (!rawId) return;
      
      // Store start position for delta tracking
      const startEvt = event.activatorEvent as any;
      if (startEvt && 'clientX' in startEvt && 'clientY' in startEvt) {
        dragStartClientRef.current = { x: startEvt.clientX, y: startEvt.clientY };
      }
      
      // Initialize collision detector with midline approach
      const detector = new MidlineCollisionDetector({
        layoutDirection: doc.layoutDirection ?? 'right',
        axisPadding: 32,
        crossPadding: 24,
        parentZoneDepth: 4,
      });
      
      const collisionNodes = buildCollisionNodes();
      detector.buildStructures(rawId, collisionNodes, doc.rootId);
      collisionDetectorRef.current = detector;
      
      // Reset slot key for fresh detection
      lastSlotKeyRef.current = null;
      
      // Find original state
      const node = doc.nodes.find(n => n.id === rawId);
      const startPos = dragStartClientRef.current;
      const flowPos = startPos ? clientToFlow(startPos.x, startPos.y) : { x: 0, y: 0 };
      
      // Snapshot actual on-screen size for a pixel-perfect ghost
      let ghostSize: { width: number; height: number } | undefined;
      let ghostClone: HTMLElement | null = null;
      try {
        const el = document.querySelector(
          `[data-testid='mindmap-node'][data-node-id="${rawId}"]`
        ) as HTMLElement | null;
        if (el) {
          const rect = el.getBoundingClientRect();
          ghostSize = { width: Math.round(rect.width), height: Math.round(rect.height) };
          // Build a DOM clone for pixel-perfect overlay
          const clone = el.cloneNode(true) as HTMLElement;
          // Lock size and disable interactions
          Object.assign(clone.style, {
            width: `${ghostSize.width}px`,
            height: `${ghostSize.height}px`,
            boxSizing: 'border-box',
            pointerEvents: 'none',
            margin: '0',
          } as Partial<CSSStyleDeclaration>);
          // Inline critical computed font styles to avoid portal inheritance differences
          try {
            const cs = window.getComputedStyle(el);
            (clone.style as any).fontFamily = cs.fontFamily;
            (clone.style as any).fontSize = cs.fontSize;
            (clone.style as any).fontWeight = cs.fontWeight;
            (clone.style as any).fontStyle = cs.fontStyle;
            (clone.style as any).lineHeight = cs.lineHeight;
            (clone.style as any).letterSpacing = cs.letterSpacing;
            (clone.style as any).textTransform = cs.textTransform;
            (clone.style as any).fontFeatureSettings = (cs as any).fontFeatureSettings ?? '';
            (clone.style as any).fontVariationSettings = (cs as any).fontVariationSettings ?? '';
            (clone.style as any).textRendering = cs.textRendering as any;
            // smoothing (vendor-prefixed)
            (clone.style as any).WebkitFontSmoothing = (cs as any).WebkitFontSmoothing ?? '';
            (clone.style as any).MozOsxFontSmoothing = (cs as any).MozOsxFontSmoothing ?? '';
          } catch {}
          // Avoid duplicate test selectors while dragging
          clone.removeAttribute('data-testid');
          ghostClone = clone;
        }
      } catch {}

      setDragState({
        draggedId: rawId,
        originalParentId: node?.parentId ?? null,
        originalIndex: node?.order ?? 0,
        dropTarget: null,
        pointerScreen: startPos ?? { x: 0, y: 0 },
        pointerFlow: flowPos,
        ghostSize,
        ghostClone,
      });

      try {
        const dims = nodeDimensions[rawId];
        logRef.current.info('drag:start %o', {
          draggedId: rawId,
          originalParentId: node?.parentId ?? null,
          originalIndex: node?.order ?? 0,
          ghostSizeScreenPx: ghostSize ?? null,
          measuredFlowDims: dims ?? null,
        });
      } catch {}
    },
    [enabled, doc, buildCollisionNodes, clientToFlow, nodeDimensions]
  );
  
  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!enabled || !dragState || !collisionDetectorRef.current) return;
      
      const start = dragStartClientRef.current;
      if (!start) return;
      
      // Calculate current pointer position
      const delta = event.delta || { x: 0, y: 0 };
      const currentScreen = {
        x: start.x + delta.x,
        y: start.y + delta.y,
      };
      const currentFlow = clientToFlow(currentScreen.x, currentScreen.y);
      
      // Find best drop target using midline detection
      const detector = collisionDetectorRef.current;
      let dropTarget = detector.findBestTarget(
        currentFlow,
        dragState.draggedId,
        doc.rootId
      );

      if (!dropTarget && dragState.dropTarget) {
        dropTarget = dragState.dropTarget;
      }

      // Normalize parent-zone into a sibling-slot at either start or end,
      // so we can reuse the same preview + placeholder logic.
      if (dropTarget && dropTarget.type === 'parent-zone' && dropTarget.parentId) {
        const parentId = dropTarget.parentId;
        const parentPos = positions[parentId];
        const parentDims = nodeDimensions[parentId];
        const childCount = doc.nodes.filter(n => n.parentId === parentId && n.id !== dragState.draggedId).length;
        const dir = doc.layoutDirection ?? 'right';
        let index = 0;
        if (parentPos && parentDims) {
          const centerX = parentPos.x + parentDims.width / 2;
          const centerY = parentPos.y + parentDims.height / 2;
          if (dir === 'right' || dir === 'left') {
            // Vertical stack → above center => first, below => last
            index = currentFlow.y < centerY ? 0 : childCount;
          } else {
            // Horizontal stack → left of center => first, right => last
            index = currentFlow.x < centerX ? 0 : childCount;
          }
        } else {
          // Fallback: append
          index = childCount;
        }

        // Build a lightweight slot rect around the parent; ELK preview will refine position.
        const rect = parentPos && parentDims
          ? { x: parentPos.x, y: parentPos.y, width: parentDims.width, height: parentDims.height }
          : { x: currentFlow.x - 16, y: currentFlow.y - 8, width: 32, height: 16 };

        const normalized = {
          type: 'sibling-slot' as const,
          parentId,
          index,
          rect,
          distance: 0,
        };

        try {
          logRef.current.info('drag:parent-zone->slot %o', {
            parentId,
            index,
            dir,
          });
        } catch {}

        dropTarget = normalized;
      }
      
      // Build slot key for throttling
      const slotKey = dropTarget
        ? `${dropTarget.type}:${dropTarget.parentId}:${dropTarget.index}`
        : null;
      
      // Only update state if slot actually changed (throttle)
      if (slotKey !== lastSlotKeyRef.current) {
        lastSlotKeyRef.current = slotKey;
        // Log only on slot change (final target change)
        try {
          if (dropTarget) {
            logRef.current.info('drag:slot-change %o', {
              draggedId: dragState.draggedId,
              targetType: dropTarget.type,
              targetParentId: dropTarget.parentId,
              targetIndex: dropTarget.index,
              slotKey,
            });
          } else {
            logRef.current.info('drag:slot-clear %o', {
              draggedId: dragState.draggedId,
            });
          }
        } catch {}
        
        setDragState(prev => prev ? {
          ...prev,
          dropTarget,
          pointerScreen: currentScreen,
          pointerFlow: currentFlow,
        } : null);
      } else {
        // Same slot, just update pointer (for overlay)
        setDragState(prev => prev ? {
          ...prev,
          pointerScreen: currentScreen,
          pointerFlow: currentFlow,
        } : null);
      }

      // Auto-pan near edges (overlay-only; no RF state changes)
      try {
        const pane = getPaneElement();
        if (pane && typeof panViewportBy === 'function') {
          const rect = pane.getBoundingClientRect();
          const relX = currentScreen.x - 0; // client coords within pane rect
          const relY = currentScreen.y - 0;
          const AUTO_PAN_THRESHOLD = 48;
          const SPEED = 14; // px per frame

          let panX = 0;
          let panY = 0;
          // Compute pointer relative to pane
          const paneLeft = rect.left;
          const paneTop = rect.top;
          const px = start.x + (event.delta?.x || 0) - paneLeft;
          const py = start.y + (event.delta?.y || 0) - paneTop;

          if (px < AUTO_PAN_THRESHOLD) panX = SPEED; // pan right (content moves right, viewport x increases)
          else if (px > rect.width - AUTO_PAN_THRESHOLD) panX = -SPEED; // pan left
          if (py < AUTO_PAN_THRESHOLD) panY = SPEED; // pan down
          else if (py > rect.height - AUTO_PAN_THRESHOLD) panY = -SPEED; // pan up

          const needsPan = panX !== 0 || panY !== 0;

          const tick = () => {
            if (!autoPanRafRef.current) return; // cancelled
            panViewportBy(panX, panY);
            // Recompute pointerFlow using same screen coords but updated viewport
            const vp = getViewport();
            const flowX = (px - vp.x) / vp.zoom;
            const flowY = (py - vp.y) / vp.zoom;
            const detector2 = collisionDetectorRef.current!;
            const nextDrop = detector2.findBestTarget({ x: flowX, y: flowY }, dragState.draggedId, doc.rootId);
            
            // Check if slot changed during autopan
            const nextKey = nextDrop ? `${nextDrop.type}:${nextDrop.parentId}:${nextDrop.index}` : null;
            if (nextKey !== lastSlotKeyRef.current) {
              lastSlotKeyRef.current = nextKey;
            }
            
            setDragState(prev => (prev ? { ...prev, pointerFlow: { x: flowX, y: flowY }, dropTarget: nextDrop } : null));
            autoPanRafRef.current = requestAnimationFrame(tick);
          };

          if (needsPan) {
            if (autoPanRafRef.current == null) {
              autoPanRafRef.current = requestAnimationFrame(tick);
            }
          } else if (autoPanRafRef.current != null) {
            cancelAnimationFrame(autoPanRafRef.current);
            autoPanRafRef.current = null;
          }
        }
      } catch {}
    },
    [enabled, dragState, clientToFlow, doc.rootId, getPaneElement, panViewportBy, getViewport]
  );
  
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      try {
        if (!enabled || !dragState) return;
        
        const { dropTarget, draggedId, originalParentId, originalIndex } = dragState;
        try {
          logRef.current.info('drag:end %o', {
            draggedId,
            dropTarget: dropTarget
              ? { type: dropTarget.type, parentId: dropTarget.parentId, index: dropTarget.index }
              : null,
            originalParentId,
            originalIndex,
          });
        } catch {}

        // Only commit if we have a valid drop target
        if (dropTarget) {
          // No-op guard: same parent and same index
          const isNoOp = dropTarget.parentId === originalParentId && dropTarget.index === originalIndex;
          if (isNoOp) {
            try { logRef.current.info('drag:commit noop %o', { draggedId, parentId: originalParentId, index: originalIndex }); } catch {}
          } else {
            try {
              logRef.current.info('drag:commit move %o', {
                draggedId,
                parentId: dropTarget.parentId,
                index: dropTarget.index,
              });
            } catch {}
            onMove(draggedId, dropTarget.parentId, dropTarget.index);
          }
        } else {
          try { logRef.current.info('drag:commit skip %o', { draggedId }); } catch {}
        }
      } finally {
        // Always clean up
        if (autoPanRafRef.current != null) {
          cancelAnimationFrame(autoPanRafRef.current);
          autoPanRafRef.current = null;
        }
        try { dragState?.ghostClone?.remove?.(); } catch {}
        setDragState(null);
        dragStartClientRef.current = null;
        collisionDetectorRef.current = null;
        lastSlotKeyRef.current = null;
      }
    },
    [enabled, dragState, onMove]
  );
  
  const handleDragCancel = useCallback(() => {
    try {
      if (dragState) {
        logRef.current.info('drag:cancel %o', { draggedId: dragState.draggedId });
      }
    } catch {}
    if (autoPanRafRef.current != null) {
      cancelAnimationFrame(autoPanRafRef.current);
      autoPanRafRef.current = null;
    }
    try { dragState?.ghostClone?.remove?.(); } catch {}
    setDragState(null);
    dragStartClientRef.current = null;
    collisionDetectorRef.current = null;
    lastSlotKeyRef.current = null;
  }, [dragState]);

  // Escape-to-cancel
  useEffect(() => {
    if (!dragState) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDragCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dragState, handleDragCancel]);
  
  // ============================================================================
  // dnd-kit Configuration
  // ============================================================================
  
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Slightly higher to reduce accidental drags
      },
    })
  );
  
  // ============================================================================
  // Return API
  // ============================================================================
  
  return useMemo(() => ({
    // dnd-kit props to spread on DndContext
    dndContextProps: {
      sensors,
      onDragStart: handleDragStart,
      onDragMove: handleDragMove,
      onDragEnd: handleDragEnd,
      onDragCancel: handleDragCancel,
    },
    
    // Current drag state (for visual feedback)
    dragState,
    
    // Convenience flags
    isDragging: dragState !== null,
    draggedNodeId: dragState?.draggedId ?? null,
    
    // For nodes to know if they should enable drag handles
    isDragEnabled: enabled,
  }), [
    sensors,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel,
    dragState,
    enabled,
  ]);
}
